import { apiClient } from "@/lib/api-client"
import type { InvitedFriend, ReferralHistoryResponse, ReferralStats } from "../types/settings.types"

export async function sendInvite(email: string): Promise<InvitedFriend> {
  const response = await apiClient.post<{
    success: boolean
    data: InvitedFriend
    message: string
  }>("/api/invites", { email })
  return response.data.data
}

export interface FailedInvite {
  email: string
  reason: string
}

export class BulkInviteError extends Error {
  failed: FailedInvite[]
  sent: number

  constructor(message: string, failed: FailedInvite[], sent: number) {
    super(message)
    this.name = "BulkInviteError"
    this.failed = failed
    this.sent = sent
  }
}

export async function sendBulkInvites(
  emails: string[],
): Promise<{ sent: number; failed: FailedInvite[]; message: string }> {
  const response = await apiClient.post<{
    success: boolean
    data: { sent: number; failed: FailedInvite[] }
    message: string
  }>("/api/invites/bulk", { emails })

  if (!response.data.success || response.status === 207) {
    throw new BulkInviteError(
      response.data.message,
      response.data.data.failed,
      response.data.data.sent,
    )
  }

  return {
    ...response.data.data,
    message: response.data.message,
  }
}

export async function getInvites(): Promise<InvitedFriend[]> {
  const response = await apiClient.get<{
    success: boolean
    data: InvitedFriend[]
    message: string
  }>("/api/invites")
  return response.data.data
}

export async function resendInvite(inviteId: string): Promise<InvitedFriend> {
  const response = await apiClient.post<{
    success: boolean
    data: InvitedFriend
    message: string
  }>(`/api/invites/${inviteId}/resend`)
  return response.data.data
}

export async function cancelInvite(inviteId: string): Promise<InvitedFriend> {
  const response = await apiClient.post<{
    success: boolean
    data: InvitedFriend
    message: string
  }>(`/api/invites/${inviteId}/cancel`)
  return response.data.data
}

export async function getReferralStats(): Promise<ReferralStats> {
  const response = await apiClient.get<{
    success: boolean
    data: ReferralStats
    message: string
  }>("/api/users/referral-stats")
  return response.data.data
}

export async function getReferralHistory(
  filter: "all" | "joined" | "pending" | "expired" = "all",
): Promise<ReferralHistoryResponse> {
  const response = await apiClient.get<{
    success: boolean
    data: ReferralHistoryResponse
    message: string
  }>("/api/users/referral-history", { params: { filter } })
  return response.data.data
}

export async function checkInviteStatus(
  email: string,
): Promise<{ wasInvited: boolean; inviterIds: string[] }> {
  const response = await apiClient.get<{
    success: boolean
    data: { wasInvited: boolean; inviterIds: string[] }
    message: string
  }>(`/api/invites/check/${encodeURIComponent(email)}`)
  return response.data.data
}
